import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    UnorderedList,
    ListItem,
    Text,
    Select,
    Checkbox,
    Stack,
    HStack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import {
    DownloadIcon,
    ChevronRightIcon,
    InfoOutlineIcon,
    AttachmentIcon,
} from '@chakra-ui/icons';
import Papa from 'papaparse';

import Button from '../../../../components/v4/Button';
import Heading from '../../../../components/v4/Heading';
import walkthroughIds from '../walkthroughIds';
import { useSelector } from 'react-redux';
import { Alert, AlertIcon } from 'components/v4';
import {
    CSV_LIMIT_REACHED_DESCRIPTION_ERROR,
    CSV_LIMIT_REACHED_ERROR,
    getInstructionsForPlatform,
    getPlatformName,
} from 'hooks/useCSVNumbersUpdate/constants';
import LoadingIndicator from '../../../../components/v4/LoadingIndicator';
import { axios } from '@/services/axios';
import Cookies from 'js-cookie';
import PlatformCellRenderer from '../new/cellRenderer/PlatformCellRenderer';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
export const CSVPrompt = ({ sentence }) => {
    const boldPhrases = [
        'For Operator Connect, please ensure the correct Calling Profile for the region/country is assigned. Failure to do so may result in issue making calls.',
        'You are responsible for the changes you are about to make through this data import including any cost incurred as a result of number activation. Please consult your contract for all billing terms.',
        'Please provide the correct address description for importing.',
        'You are responsible for the changes you are about to make through this data import.',
        'For Operator Connect, please ensure the correct Calling Profile for the region/country is assigned. Failure to do so may result in issue making calls.',
    ];

    const isBoldSentence = boldPhrases.includes(sentence);

    return (
        <>
            {isBoldSentence ? (
                <span className="font-bold">{sentence}</span>
            ) : (
                sentence
            )}
        </>
    );
};

export default function CSVTemplateView({
    handleUploadUsersData,
    downloadCSVText,
    header,
    // eslint-disable-next-line no-unused-vars
    step1Text,
    // eslint-disable-next-line no-unused-vars
    step2Text,
    instructionsHeader,
    instructionsBody = [],
    handleDownloadTemplate,
    loading,
    csvLimit,
    enablePlatformSelection = false,
    isUserPage = false,
}) {
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { data: currentCompanyData } = useGetCurrentCompany();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const requestScope = searchParams.get('requestScope');
    const isCSNumbersPath = location.pathname.includes('/csv-numbers');

    const inputRef = useRef(null);
    const [availablePlatforms, setAvailablePlatforms] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [selectedPlatformName, setSelectedPlatformName] = useState('');
    const [platformLoading, setPlatformLoading] = useState(false);
    const [confirmation2Checked, setConfirmation2Checked] = useState(false);
    const [instructions, setInstructions] = useState([]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileRowCount, setFileRowCount] = useState(null);
    const [filenameError, setFilenameError] = useState(null);

    useEffect(() => {
        if (enablePlatformSelection) {
            setPlatformLoading(true);
            axios
                .get(`/Company/${currentCompany}/Platforms/Available`)
                .then((response) => {
                    const makeUniqueArrayOfObjects = (arr, uniqueKey) => {
                        const uniqueMap = new Map();
                        return arr.filter((item) => {
                            if (!uniqueMap.has(item[uniqueKey])) {
                                uniqueMap.set(item[uniqueKey], true);
                                return true;
                            }
                            return false;
                        });
                    };

                    const formatPlatformName = (name) => {
                        if (name === 'EightByEight') return '8x8';
                        if (name.includes('_')) {
                            return name
                                .split('_')
                                .map(
                                    (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1).toLowerCase(),
                                )
                                .join(' ');
                        }
                        return name;
                    };

                    let uniquePlatforms = makeUniqueArrayOfObjects(
                        response.data,
                        'id',
                    ).map((platform) => ({
                        ...platform,
                        name: formatPlatformName(platform.name),
                    }));

                    if (!isUserPage) {
                        let sortedPlatforms = [];
                        const numbersPlatform = {
                            id: 'numbers',
                            name: 'Numbers',
                        };
                        sortedPlatforms.push(numbersPlatform);

                        const platformsWithoutNumbers = uniquePlatforms.filter(
                            (platform) => platform.id !== 'numbers',
                        );
                        if (platformsWithoutNumbers.length > 1) {
                            sortedPlatforms.push({
                                id: 'priority',
                                name: 'Priority',
                            });
                        }

                        sortedPlatforms.push(
                            ...uniquePlatforms.map((p) =>
                                p.id ===
                                    'b8369b29-823d-4b3d-a12f-4f708993d0a8' ||
                                p.name === 'GCC High Direct Routing' ||
                                p.name === 'Gcc High Direct Routing'
                                    ? { ...p, name: 'Microsoft Teams GCC High' }
                                    : p,
                            ),
                        );
                        uniquePlatforms = sortedPlatforms;
                    } else {
                        uniquePlatforms = uniquePlatforms.map((item) => {
                            if (
                                String(item.id).trim().toLowerCase() ===
                                'fac64979-385b-42ce-8b1c-d30e87822304'
                            ) {
                                return {
                                    ...item,
                                    name: 'Microsoft Teams Users',
                                };
                            }
                            if (
                                item.id ===
                                    'b8369b29-823d-4b3d-a12f-4f708993d0a8' ||
                                item.name === 'GCC High Direct Routing' ||
                                item.name === 'Gcc High Direct Routing'
                            ) {
                                return {
                                    ...item,
                                    name: 'Microsoft Teams GCC High',
                                };
                            }
                            return item;
                        });
                        uniquePlatforms = uniquePlatforms.filter(
                            (platform) =>
                                platform.name !== 'SBCaaS' &&
                                platform.name !== 'Microsoft Acs',
                        );
                        uniquePlatforms.sort((a, b) => {
                            if (a.name === 'Microsoft Teams Users') return -1;
                            if (b.name === 'Microsoft Teams Users') return 1;
                            return a.name.localeCompare(b.name);
                        });

                        const msTeamsIndex = uniquePlatforms.findIndex(
                            (p) => p.name === 'Microsoft Teams Users',
                        );
                        if (msTeamsIndex !== -1) {
                            uniquePlatforms.splice(msTeamsIndex + 1, 0, {
                                id: 'ms_resource_account',
                                name: 'Microsoft Resource Account',
                            });
                        }

                        const gcchIndex = uniquePlatforms.findIndex(
                            (p) =>
                                p.id ===
                                    'b8369b29-823d-4b3d-a12f-4f708993d0a8' ||
                                p.name === 'GCC High Direct Routing' ||
                                p.name === 'Gcc High Direct Routing',
                        );
                        if (gcchIndex !== -1) {
                            uniquePlatforms.splice(gcchIndex + 1, 0, {
                                id: 'ms_resource_account_gcch',
                                name: 'Microsoft Resource Account GCC High',
                            });
                        }
                    }
                    setAvailablePlatforms(uniquePlatforms);
                })
                .catch((error) => {
                    console.error('Error fetching platforms:', error);
                })
                .finally(() => {
                    setPlatformLoading(false);
                });
        }
    }, [currentCompany, requestScope, enablePlatformSelection, isUserPage]);

    const handlePlatformSelect = (selectedId) => {
        setSelectedPlatform(selectedId);
        const name =
            availablePlatforms.find((p) => p.id.toString() === selectedId)
                ?.name || '';
        setSelectedPlatformName(name);

        if (isUserPage) {
            if (selectedId === 'ms_resource_account') {
                Cookies.set(
                    'selectedPlatformId',
                    'fac64979-385b-42ce-8b1c-d30e87822304',
                );
                Cookies.set('selectedPlatformName', 'ms_resource_account');
            } else if (selectedId === 'ms_resource_account_gcch') {
                Cookies.set(
                    'selectedPlatformId',
                    'b8369b29-823d-4b3d-a12f-4f708993d0a8',
                );
                Cookies.set('selectedPlatformName', 'ms_resource_account_gcch');
            } else {
                Cookies.set('selectedPlatformId', selectedId);
                Cookies.set('selectedPlatformName', name);
            }
        } else {
            Cookies.set('testPlatformName', 'staticValue');
            Cookies.set(
                'selectedPlatformId',
                selectedId === 'priority' ? 'priority' : selectedId,
            );
            Cookies.set(
                'selectedPlatformName',
                selectedId === 'priority' ? 'Priority' : name,
            );
        }

        let result = getInstructionsForPlatform(name, isUserPage);
        setInstructions(result);
        setConfirmation2Checked(false);
        setUploadedFile(null);
        setFileRowCount(null);
        setFilenameError(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleConfirmation2Change = (event) => {
        setConfirmation2Checked(event.target.checked);
    };

    // Ignores separator style (spaces/underscores/case) so this doesn't
    // false-positive against the different filename conventions the various
    // templates use — see useCSVNumbersUpdate/useCSVTemplate.
    const normalizeForFilenameCheck = (str) =>
        (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    // The selected type/platform must always appear in the uploaded
    // filename — e.g. if "Microsoft Teams" is selected, uploading a Numbers
    // (or any other platform's) file must be rejected even though it's a
    // valid CSV. Once a type is selected, that's the primary safeguard; the
    // company name is only checked as a fallback before a type is picked.
    const validateFilename = (file) => {
        if (isAddressPage || !enablePlatformSelection) return null;
        const typeKey = normalizeForFilenameCheck(selectedPlatformName);
        const companyKey = normalizeForFilenameCheck(currentCompanyData?.name);
        if (!typeKey && !companyKey) return null;

        const nameKey = normalizeForFilenameCheck(file?.name);
        const mismatch = typeKey
            ? !nameKey.includes(typeKey)
            : !nameKey.includes(companyKey);

        if (mismatch) {
            return `Incorrect filename uploaded. Please check the file is named ${
                currentCompanyData?.name || 'CompanyName'
            }_${selectedPlatformName || 'Type'} (matching the downloaded template).`;
        }
        return null;
    };

    const onDrop = (acceptedFiles) => {
        if (window.location.pathname.includes('/addresses/csv-address')) {
            const file = acceptedFiles[0];
            setUploadedFile(file);
            setFilenameError(null);
            countCSVRows(file);
        } else {
            if (acceptedFiles && acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                const error = validateFilename(file);
                setFilenameError(error);
                if (error) return;
                setUploadedFile(file);
                countCSVRows(file);
            }
        }
    };

    const countCSVRows = (file) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                setFileRowCount(results.data.length);
            },
            error: () => {
                setFileRowCount(null);
            },
        });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        multiple: false,
    });

    const handleValidate = () => {
        if (window.location.href.includes('/addresses/csv-address')) {
            const event = { target: { files: [uploadedFile] } };
            handleUploadUsersData(event);
        } else {
            if (uploadedFile) {
                const event = { target: { files: [uploadedFile] } };
                handleUploadUsersData(event);
            }
        }
    };

    const isAddressPage = window.location.pathname.includes(
        '/addresses/csv-address',
    );
    const showContent =
        selectedPlatformName || isAddressPage || !enablePlatformSelection;

    const isValidateDisabled = isAddressPage
        ? false
        : !uploadedFile ||
          (isCSNumbersPath && !isUserPage && !confirmation2Checked);

    if (platformLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Box display="flex" justifyContent="start" width="100%">
            <Box
                width="100%"
                maxWidth="700px"
                display="flex"
                flexDirection="column"
                gap="0"
                fontFamily="Inter"
                boxShadow="base"
                p="8"
                bg={darkMode ? 'dark.tcap' : 'white'}
                rounded="md">
                <Heading size="lg" mb={4}>
                    {header}
                </Heading>

                {/* Top-level blue info box */}
                <Box
                    p={3}
                    mb={4}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                    display="flex"
                    alignItems="flex-start"
                    gap={2}>
                    <InfoOutlineIcon color="blue.500" mt="2px" flexShrink={0} />
                    <Text fontSize="sm" color="blue.800">
                        {isUserPage
                            ? 'This tool will allow you to bulk update the User details for your company.'
                            : 'This tool will allow you to bulk update the Number details for your company.'}
                    </Text>
                </Box>

                {/* Step 1: Select Platform */}
                {enablePlatformSelection && (
                    <Box mb={6}>
                        <Text fontWeight="bold" mb={2}>
                            1. Select platform
                        </Text>
                        <Select
                            placeholder="Select platform"
                            onChange={(e) =>
                                handlePlatformSelect(e.target.value)
                            }
                            color={darkMode ? 'dark.tcap' : ''}>
                            {availablePlatforms.map((platform) => (
                                <option key={platform.id} value={platform.id}>
                                    {platform.name}
                                </option>
                            ))}
                        </Select>
                    </Box>
                )}

                {showContent && (
                    <>
                        {/* Platform notes box */}
                        {selectedPlatformName && (
                            <Box mb={6} p={3} bg="blue.50" borderRadius="md">
                                <HStack spacing={2} alignItems="center" mb={2}>
                                    <PlatformCellRenderer
                                        platformName={false}
                                        value={selectedPlatform}
                                    />
                                    <Text
                                        fontWeight="semibold"
                                        color="blue.800"
                                        fontSize="sm">
                                        {selectedPlatformName}
                                    </Text>
                                </HStack>
                                <Box fontSize="sm" color="blue.700">
                                    {getPlatformName(selectedPlatformName)}
                                </Box>

                                {/* Changeable / Locked fields accordion */}
                                {instructions.length > 0 && (
                                    <Accordion allowToggle mt={3}>
                                        <AccordionItem border="none">
                                            <AccordionButton
                                                px={0}
                                                py={1}
                                                _hover={{ bg: 'transparent' }}
                                                _focus={{ boxShadow: 'none' }}
                                                color="blue.700"
                                                fontSize="sm"
                                                fontWeight="semibold">
                                                <Box flex="1" textAlign="left">
                                                    Notes &amp; Instructions
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel pb={2} px={0}>
                                                <UnorderedList
                                                    spacing={1}
                                                    fontSize="sm"
                                                    color="blue.800">
                                                    {instructions.map(
                                                        (v, i) => (
                                                            <ListItem key={i}>
                                                                <CSVPrompt
                                                                    sentence={v}
                                                                />
                                                            </ListItem>
                                                        ),
                                                    )}
                                                </UnorderedList>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    </Accordion>
                                )}

                                {isAddressPage &&
                                    instructionsBody.length > 0 && (
                                        <Accordion allowToggle mt={3}>
                                            <AccordionItem border="none">
                                                <AccordionButton
                                                    px={0}
                                                    py={1}
                                                    _hover={{
                                                        bg: 'transparent',
                                                    }}
                                                    _focus={{
                                                        boxShadow: 'none',
                                                    }}
                                                    color="blue.700"
                                                    fontSize="sm"
                                                    fontWeight="semibold">
                                                    <Box
                                                        flex="1"
                                                        textAlign="left">
                                                        {instructionsHeader}
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel pb={2} px={0}>
                                                    <UnorderedList
                                                        spacing={1}
                                                        fontSize="sm"
                                                        color="blue.800">
                                                        {instructionsBody.map(
                                                            (
                                                                instruction,
                                                                index,
                                                            ) => (
                                                                <ListItem
                                                                    key={index}>
                                                                    <CSVPrompt
                                                                        sentence={
                                                                            instruction
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            ),
                                                        )}
                                                    </UnorderedList>
                                                </AccordionPanel>
                                            </AccordionItem>
                                        </Accordion>
                                    )}
                            </Box>
                        )}

                        {/* Step 2: Download CSV */}
                        <Box mb={6}>
                            <Text fontWeight="bold" mb={2}>
                                2. Download CSV template with any existing data
                            </Text>
                            <Button
                                isLoading={loading}
                                leftIcon={<DownloadIcon />}
                                sx={{ width: '160px' }}
                                size="sm"
                                onClick={handleDownloadTemplate}
                                data-walkthroughid={
                                    walkthroughIds.csv.ufDownloadBtn
                                }>
                                {downloadCSVText}
                            </Button>
                        </Box>

                        {/* Step 3: Confirmation (Numbers only, not address page) */}
                        {!isUserPage && !isAddressPage && (
                            <Box mb={6}>
                                <Text fontWeight="bold" mb={2}>
                                    3. Confirmation &amp; responsibility
                                </Text>
                                <Box
                                    p={3}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="red.300"
                                    bg="red.50">
                                    <Stack spacing={0}>
                                        <Checkbox
                                            isChecked={confirmation2Checked}
                                            onChange={handleConfirmation2Change}
                                            alignItems="flex-start"
                                            colorScheme="red"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                '& > span:first-of-type': {
                                                    marginTop: '4px',
                                                },
                                                '& > span:last-of-type': {
                                                    lineHeight: '1.5',
                                                },
                                            }}>
                                            <Text fontSize="sm" color="red.800">
                                                I confirm I am responsible for
                                                the changes made through this
                                                data import including any cost
                                                incurred as a result of number
                                                activation. Please consult your
                                                contract for all billing terms.
                                            </Text>
                                        </Checkbox>
                                    </Stack>
                                </Box>
                            </Box>
                        )}

                        {/* Step 4: Upload CSV */}
                        <Box mb={6}>
                            <Text fontWeight="bold" mb={2}>
                                {!isUserPage && !isAddressPage ? '4. ' : '3. '}
                                Upload CSV to be imported
                            </Text>

                            {uploadedFile ? (
                                <Box
                                    border="2px dashed"
                                    borderColor="green.400"
                                    borderRadius="md"
                                    p={6}
                                    textAlign="center"
                                    bg="green.50"
                                    position="relative">
                                    <AttachmentIcon
                                        boxSize={8}
                                        color="green.500"
                                        mb={2}
                                    />
                                    <Text
                                        color="green.700"
                                        fontWeight="bold"
                                        fontSize="md">
                                        {uploadedFile.name}
                                    </Text>
                                    <Text fontSize="sm" color="green.600">
                                        {fileRowCount !== null
                                            ? `Valid CSV • ${fileRowCount} Row${fileRowCount !== 1 ? 's' : ''}`
                                            : 'File ready to validate'}
                                    </Text>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        mt={2}
                                        onClick={() => {
                                            setUploadedFile(null);
                                            setFileRowCount(null);
                                            setFilenameError(null);
                                        }}>
                                        Remove
                                    </Button>
                                </Box>
                            ) : (
                                <Box
                                    {...getRootProps()}
                                    border="2px dashed"
                                    borderColor={
                                        isDragActive ? 'blue.400' : 'gray.200'
                                    }
                                    borderRadius="md"
                                    p={8}
                                    textAlign="center"
                                    bg={isDragActive ? 'blue.50' : 'gray.50'}
                                    cursor="pointer"
                                    transition="border-color 0.2s, background 0.2s">
                                    <input {...getInputProps()} />
                                    <AttachmentIcon
                                        boxSize={8}
                                        color="gray.400"
                                        mb={2}
                                    />
                                    <Text
                                        color="blue.600"
                                        fontWeight="bold"
                                        fontSize="md">
                                        Click to upload{' '}
                                        <span style={{ color: '#666' }}>
                                            or drag and drop
                                        </span>
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        Only upload a CSV file with up to 1000
                                        Rows
                                    </Text>
                                </Box>
                            )}
                        </Box>

                        {/* Filename validation error */}
                        {filenameError && (
                            <Alert status="error" mb={4}>
                                <AlertIcon />
                                <Text fontSize="sm">{filenameError}</Text>
                            </Alert>
                        )}

                        {/* CSV limit error */}
                        {csvLimit && (
                            <Alert status="error" mb={4}>
                                <AlertIcon />
                                <Box flexDirection="column">
                                    <Text fontWeight="bold">
                                        {CSV_LIMIT_REACHED_ERROR}
                                    </Text>
                                    {CSV_LIMIT_REACHED_DESCRIPTION_ERROR}
                                </Box>
                            </Alert>
                        )}

                        {/* Navigation Buttons */}
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mt={4}>
                            <Button
                                variant="outline"
                                sx={{ width: '120px', bgColor: 'white' }}
                                size="sm"
                                _hover={{ opacity: 0.8 }}
                                onClick={() => navigate(-1)}
                                data-walkthroughid={
                                    walkthroughIds.csv.ufCloseBtn
                                }>
                                Back
                            </Button>
                            <Button
                                sx={{ width: '140px' }}
                                size="sm"
                                rightIcon={<ChevronRightIcon />}
                                onClick={handleValidate}
                                isDisabled={isValidateDisabled}>
                                Validate file
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}
